import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import type { ChatMessage } from './use-ai-builder-chat';

type ScrollMode = 'initial' | 'pinned' | 'history';

type AnchorPosition = {
  id: string;
  top: number;
};

type UseChatScrollControllerOptions = {
  containerRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  messages: ChatMessage[];
  isReady: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  sessionKey?: string;
  bottomThreshold?: number;
  topThreshold?: number;
};

export const useChatScrollController = ({
  containerRef,
  contentRef,
  messages,
  isReady,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  sessionKey,
  bottomThreshold = 50,
  topThreshold = 150,
}: UseChatScrollControllerOptions) => {
  const modeRef = useRef<ScrollMode>('initial');
  const savedAnchorRef = useRef<AnchorPosition | null>(null);
  const prevMessageCountRef = useRef(0);
  const isFetchingOlderRef = useRef(false);

  useEffect(() => {
    modeRef.current = 'initial';
    savedAnchorRef.current = null;
    prevMessageCountRef.current = 0;
    isFetchingOlderRef.current = false;
  }, [sessionKey]);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const container = containerRef.current;
      if (!container) return;
      container.scrollTo({ top: container.scrollHeight, behavior });
      modeRef.current = 'pinned';
    },
    [containerRef],
  );

  const pauseAutoScroll = useCallback(() => {
    modeRef.current = 'history';
  }, []);

  const saveAnchor = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const messageNodes = container.querySelectorAll<HTMLElement>('[data-message-id]');
    if (!messageNodes.length) return;

    const containerTop = container.getBoundingClientRect().top;

    for (const node of messageNodes) {
      const rect = node.getBoundingClientRect();
      const top = rect.top - containerTop;
      const bottom = top + rect.height;
      if (bottom >= 0) {
        const id = node.dataset.messageId;
        if (id) {
          savedAnchorRef.current = { id, top };
        }
        return;
      }
    }

    const firstNode = messageNodes[0];
    const firstId = firstNode.dataset.messageId;
    if (!firstId) return;
    const firstTop = firstNode.getBoundingClientRect().top - containerTop;
    savedAnchorRef.current = { id: firstId, top: firstTop };
  }, [containerRef]);

  const restoreAnchor = useCallback(() => {
    const container = containerRef.current;
    const savedAnchor = savedAnchorRef.current;
    if (!container || !savedAnchor) return;

    const anchorElement = container.querySelector<HTMLElement>(
      `[data-message-id="${savedAnchor.id}"]`,
    );
    if (!anchorElement) {
      savedAnchorRef.current = null;
      return;
    }

    const containerTop = container.getBoundingClientRect().top;
    const newTop = anchorElement.getBoundingClientRect().top - containerTop;
    const diff = newTop - savedAnchor.top;

    if (diff !== 0) {
      container.scrollTop += diff;
    }
    savedAnchorRef.current = null;
  }, [containerRef]);

  const onScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const isPinned = distanceFromBottom <= bottomThreshold;
    modeRef.current = isPinned ? 'pinned' : 'history';

    const isNearTop = container.scrollTop <= topThreshold;
    if (isNearTop && hasNextPage && !isFetchingNextPage && !isFetchingOlderRef.current) {
      isFetchingOlderRef.current = true;
      saveAnchor();
      fetchNextPage();
    }
  }, [
    containerRef,
    bottomThreshold,
    topThreshold,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    saveAnchor,
  ]);

  useLayoutEffect(() => {
    if (!isFetchingNextPage && isFetchingOlderRef.current) {
      isFetchingOlderRef.current = false;
      restoreAnchor();
    }
  }, [isFetchingNextPage, restoreAnchor]);

  useLayoutEffect(() => {
    if (!isReady) return;
    if (messages.length === 0) {
      prevMessageCountRef.current = 0;
      modeRef.current = 'initial';
      return;
    }

    const messageCount = messages.length;
    if (modeRef.current === 'initial') {
      scrollToBottom('auto');
      prevMessageCountRef.current = messageCount;
      return;
    }

    if (modeRef.current === 'pinned' && messageCount > prevMessageCountRef.current) {
      scrollToBottom('auto');
    }

    prevMessageCountRef.current = messageCount;
  }, [isReady, messages.length, scrollToBottom]);

  useEffect(() => {
    if (!isReady) return;
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const observer = new ResizeObserver(() => {
      if (modeRef.current === 'pinned') {
        container.scrollTop = container.scrollHeight;
      }
    });

    observer.observe(content);
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, contentRef, isReady]);

  return {
    onScroll,
    scrollToBottom,
    pauseAutoScroll,
  };
};
